import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, ClipboardList, UserCog, FileText, Receipt, BarChart3,
  MapPin, Calendar, AlertTriangle, ChevronRight, FileCheck2, ShieldCheck, Search,
  Sparkles, X, ArrowLeft, ArrowRight,
} from "lucide-react";

const STATUS = {
  new: { label: "New", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  assigned: { label: "Assigned", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  accepted: { label: "Accepted", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  in_progress: { label: "In Progress", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  attempted: { label: "Attempted", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  served: { label: "Served", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  non_service: { label: "Non-Service", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  awaiting_return: { label: "Awaiting Return", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  completed: { label: "Completed", cls: "bg-emerald-600 text-white border-emerald-600" },
  billing_pending: { label: "Billing Pending", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  needs_attention: { label: "Needs Attention", cls: "bg-red-50 text-red-700 border-red-200" },
};

const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));
const shortDate = (s) => (s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—");

const StatusBadge = ({ s }) => {
  const m = STATUS[s] || { label: s, cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>{m.label}</span>;
};

const STAT_BORDER = { slate: "border-slate-200", red: "border-red-200", orange: "border-orange-200", emerald: "border-emerald-200" };
const Stat = ({ label, value, tone = "slate", testid }) => (
  <div className={`rounded-2xl border bg-white p-5 shadow-sm ${STAT_BORDER[tone] || STAT_BORDER.slate}`} data-testid={testid}>
    <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</p>
    <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
  </div>
);

const useDemo = (path, deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(path); setData(data); } catch { setData(null); }
    finally { setLoading(false); }
  }, [path]); // eslint-disable-line
  useEffect(() => { load(); }, deps); // eslint-disable-line
  return { data, loading, reload: load };
};

const Loading = () => <div className="grid gap-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}</div>;

/* ---------------- Dashboard ---------------- */
function DashboardView() {
  const { data, loading } = useDemo("/agency/demo/overview");
  if (loading) return <Loading />;
  if (!data) return null;
  const order = Object.keys(STATUS);
  return (
    <div className="space-y-6" data-testid="demo-dashboard">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Active Clients" value={data.clients} testid="stat-clients" />
        <Stat label="Providers" value={data.providers} testid="stat-providers" />
        <Stat label="Assignments" value={data.assignments} testid="stat-assignments" />
        <Stat label="Needs Attention" value={data.needs_attention} tone="red" testid="stat-attention" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Gross Charged" value={money(data.gross_charged)} testid="stat-gross" />
        <Stat label="ARPS Revenue" value={money(data.arps_revenue)} testid="stat-revenue" />
        <Stat label="Provider Payout" value={money(data.provider_payout)} testid="stat-payout" />
        <Stat label="Outstanding" value={money(data.outstanding)} tone="orange" testid="stat-outstanding" />
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="dashboard-pipeline">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Assignment pipeline</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {order.filter((s) => data.by_status[s]).map((s) => (
            <div key={s} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2" data-testid={`pipeline-${s}`}>
              <StatusBadge s={s} />
              <span className="text-sm font-bold text-slate-900">{data.by_status[s]}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <Calendar className="h-4 w-4" /> {data.deadlines_soon} assignment(s) have a deadline within 3 days.
        </div>
      </div>
    </div>
  );
}

/* ---------------- Clients ---------------- */
function ClientsView() {
  const { data, loading } = useDemo("/agency/demo/clients");
  if (loading) return <Loading />;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="demo-clients">
      <Table>
        <TableHeader><TableRow><TableHead>Client</TableHead><TableHead>County</TableHead><TableHead>Email</TableHead><TableHead className="text-right">Assignments</TableHead></TableRow></TableHeader>
        <TableBody>
          {(data || []).map((c) => (
            <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
              <TableCell className="font-medium text-slate-900">{c.name}</TableCell>
              <TableCell className="text-slate-600">{c.county}</TableCell>
              <TableCell className="text-slate-500">{c.email}</TableCell>
              <TableCell className="text-right font-semibold">{c.assignments}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/* ---------------- Providers ---------------- */
function ProvidersView() {
  const { data, loading } = useDemo("/agency/demo/providers");
  if (loading) return <Loading />;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="demo-providers">
      <Table>
        <TableHeader><TableRow><TableHead>Provider</TableHead><TableHead>Service Area</TableHead><TableHead>Phone</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Assignments</TableHead><TableHead className="text-right">Payout</TableHead></TableRow></TableHeader>
        <TableBody>
          {(data || []).map((p) => (
            <TableRow key={p.id} data-testid={`provider-row-${p.id}`}>
              <TableCell className="font-medium text-slate-900">{p.name}</TableCell>
              <TableCell className="text-slate-600"><MapPin className="mr-1 inline h-3.5 w-3.5 text-slate-400" />{p.service_area}</TableCell>
              <TableCell className="text-slate-500">{p.phone}</TableCell>
              <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${p.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{p.status}</span></TableCell>
              <TableCell className="text-right font-semibold">{p.assignments}</TableCell>
              <TableCell className="text-right font-semibold text-slate-900">{money(p.payout)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/* ---------------- Assignments ---------------- */
function AssignmentsView({ onOpen }) {
  const [status, setStatus] = useState("");
  const [providerId, setProviderId] = useState("");
  const [area, setArea] = useState("");
  const [q, setQ] = useState("");
  const { data: providers } = useDemo("/agency/demo/providers");
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  if (providerId) qs.set("provider_id", providerId);
  if (area) qs.set("area", area);
  const { data, loading } = useDemo(`/agency/demo/assignments${qs.toString() ? `?${qs}` : ""}`, [status, providerId, area]);
  const areas = [...new Set((providers || []).map((p) => p.service_area).filter(Boolean))].sort();
  const rows = (data || []).filter((r) => !q || (r.job_number + r.client + (r.defendant || "")).toLowerCase().includes(q.toLowerCase()));
  const clearAll = () => { setStatus(""); setProviderId(""); setArea(""); setQ(""); };
  const active = status || providerId || area || q;
  return (
    <div className="space-y-4" data-testid="demo-assignments">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="assignments-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search job #, client, defendant…" className="pl-9" />
        </div>
        <select data-testid="assignments-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
          <option value="">All statuses</option>
          {Object.keys(STATUS).map((s) => <option key={s} value={s}>{STATUS[s].label}</option>)}
        </select>
        <select data-testid="assignments-provider-filter" value={providerId} onChange={(e) => setProviderId(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
          <option value="">All providers</option>
          {(providers || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select data-testid="assignments-area-filter" value={area} onChange={(e) => setArea(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
          <option value="">All service areas</option>
          {areas.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        {active && <button data-testid="assignments-clear-filters" onClick={clearAll} className="text-xs font-medium text-slate-400 hover:text-slate-600">Clear</button>}
      </div>
      <p className="text-xs text-slate-400" data-testid="assignments-count">{rows.length} assignment{rows.length === 1 ? "" : "s"}</p>
      {loading ? <Loading /> : (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <Table>
            <TableHeader><TableRow><TableHead>Job #</TableHead><TableHead>Client</TableHead><TableHead>Defendant</TableHead><TableHead>Provider</TableHead><TableHead>Service Area</TableHead><TableHead>Type</TableHead><TableHead>Deadline</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Charged</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`assignment-row-${r.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => onOpen(r.id)}>
                  <TableCell className="font-mono text-xs font-semibold text-slate-900">{r.job_number}</TableCell>
                  <TableCell className="text-slate-700">{r.client}</TableCell>
                  <TableCell className="text-slate-600">{r.defendant}</TableCell>
                  <TableCell className="text-slate-500">{r.provider}</TableCell>
                  <TableCell className="text-slate-500">{r.service_area || "—"}</TableCell>
                  <TableCell className="text-slate-500">{r.service_type}</TableCell>
                  <TableCell className="text-slate-500">{shortDate(r.deadline)}</TableCell>
                  <TableCell><StatusBadge s={r.status} /></TableCell>
                  <TableCell className="text-right font-semibold">{money(r.charged)}</TableCell>
                  <TableCell><ChevronRight className="h-4 w-4 text-slate-300" /></TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={10} className="py-8 text-center text-sm text-slate-400">No assignments match.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Assignment detail (lifecycle) ---------------- */
function LifecycleStep({ icon: Icon, title, children, done }) {
  return (
    <div className="relative flex gap-4 pb-6 last:pb-0" data-testid={`lifecycle-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex flex-col items-center">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}><Icon className="h-4 w-4" /></div>
        <div className="mt-1 w-px flex-1 bg-slate-200 last:hidden" />
      </div>
      <div className="flex-1 pt-1">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{title}</p>
        <div className="mt-1 text-sm text-slate-700">{children}</div>
      </div>
    </div>
  );
}

function AssignmentDetail({ id, onClose }) {
  const { data: r, loading } = useDemo(`/agency/demo/assignments/${id}`, [id]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="assignment-detail">
        <DialogHeader className="sr-only">
          <DialogTitle>Assignment detail</DialogTitle>
          <DialogDescription>Full service lifecycle for this assignment.</DialogDescription>
        </DialogHeader>
        {loading || !r ? <Loading /> : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between gap-3">
                <span className="font-mono">{r.job_number}</span><StatusBadge s={r.status} />
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-500">Case {r.case_number} · {r.defendant} · {r.service_type}</p>
            <div className="mt-4">
              <LifecycleStep icon={Users} title="Client" done>
                <span className="font-medium text-slate-900">{r.client?.name}</span> — {r.client?.county}
              </LifecycleStep>
              <LifecycleStep icon={ClipboardList} title="Assignment" done>
                <MapPin className="mr-1 inline h-3.5 w-3.5 text-slate-400" />{r.service_address} · Deadline {shortDate(r.deadline)}
              </LifecycleStep>
              <LifecycleStep icon={FileText} title="Documents" done={r.documents?.length > 0}>
                {(r.documents || []).map((d, i) => <span key={i} className="mr-2 inline-flex items-center gap-1 rounded bg-slate-50 px-2 py-0.5 text-xs">{d.kind === "return" ? <FileCheck2 className="h-3 w-3" /> : <FileText className="h-3 w-3" />}{d.name}</span>)}
              </LifecycleStep>
              <LifecycleStep icon={UserCog} title="Provider" done={!!r.provider}>
                {r.provider ? <><span className="font-medium text-slate-900">{r.provider.name}</span> — {r.provider.service_area}</> : <span className="text-slate-400">Unassigned</span>}
              </LifecycleStep>
              <LifecycleStep icon={ClipboardList} title="Attempts" done={r.attempts?.length > 0}>
                {(r.attempts || []).length === 0 ? <span className="text-slate-400">No attempts yet</span> : (
                  <div className="space-y-1.5">
                    {r.attempts.map((a) => (
                      <div key={a.n} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-2 py-1 text-xs">
                        <span className="font-semibold">#{a.n}</span><span>{shortDate(a.at)}</span>
                        <span className={`rounded px-1.5 py-0.5 font-semibold ${a.outcome === "served" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{a.outcome}</span>
                        <span className="text-slate-500">{a.notes}</span>
                      </div>
                    ))}
                  </div>
                )}
              </LifecycleStep>
              <LifecycleStep icon={ShieldCheck} title="Evidence" done={(r.attempts || []).some((a) => a.photo || a.gps)}>
                {(r.attempts || []).map((a) => (
                  <span key={a.n} className="mr-2 inline-flex items-center gap-1 rounded bg-slate-50 px-2 py-0.5 text-xs">
                    #{a.n}: {a.gps ? `GPS ${a.gps.lat.toFixed(3)}, ${a.gps.lng.toFixed(3)}` : "no GPS"}{a.photo ? " · 📷 photo" : ""}
                  </span>
                ))}
              </LifecycleStep>
              <LifecycleStep icon={FileCheck2} title="Return" done={r.return?.status === "generated"}>
                {r.return ? <><span className="font-medium text-slate-900">{r.return.type}</span> — {r.return.status}{r.return.generated_at ? ` · ${shortDate(r.return.generated_at)}` : ""}</> : <span className="text-slate-400">Not yet generated</span>}
              </LifecycleStep>
              <LifecycleStep icon={Receipt} title="Billing" done={r.billing?.status === "paid"}>
                Base {money(r.billing?.base)}{r.billing?.rush ? ` · Rush ${money(r.billing.rush)}` : ""}{r.billing?.addons ? ` · Add-ons ${money(r.billing.addons)}` : ""} = <span className="font-semibold text-slate-900">{money(r.billing?.charged)}</span> · <span className="uppercase">{r.billing?.status}</span>
              </LifecycleStep>
              <LifecycleStep icon={BarChart3} title="Compensation" done={r.compensation?.status === "paid"}>
                Provider payout <span className="font-semibold text-slate-900">{money(r.compensation?.provider_payout)}</span> · ARPS revenue {money(r.compensation?.arps_revenue)} · <span className="uppercase">{r.compensation?.status}</span>
              </LifecycleStep>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Documents ---------------- */
function DocumentsView() {
  const { data, loading } = useDemo("/agency/demo/documents");
  if (loading) return <Loading />;
  return (
    <div className="space-y-4" data-testid="demo-documents">
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Service Documents" value={data?.counts.service ?? 0} testid="doc-service" />
        <Stat label="Returns of Service" value={data?.counts.return ?? 0} testid="doc-return" />
        <Stat label="Signed Affidavits" value={data?.counts.affidavit ?? 0} testid="doc-affidavit" />
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <Table>
          <TableHeader><TableRow><TableHead>Document</TableHead><TableHead>Type</TableHead><TableHead>Job #</TableHead><TableHead>Client</TableHead><TableHead>Defendant</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
          <TableBody>
            {(data?.documents || []).slice(0, 200).map((d, i) => (
              <TableRow key={i} data-testid={`document-row-${i}`}>
                <TableCell className="font-medium text-slate-900">{d.kind === "return" ? <FileCheck2 className="mr-1 inline h-4 w-4 text-emerald-600" /> : <FileText className="mr-1 inline h-4 w-4 text-slate-400" />}{d.name}</TableCell>
                <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${d.kind === "return" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{d.kind === "return" ? "Return" : "Service"}</span></TableCell>
                <TableCell className="font-mono text-xs">{d.job_number}</TableCell>
                <TableCell className="text-slate-600">{d.client}</TableCell>
                <TableCell className="text-slate-600">{d.defendant}</TableCell>
                <TableCell className="text-slate-500">{shortDate(d.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* ---------------- Billing ---------------- */
function BillingView() {
  const { data, loading } = useDemo("/agency/demo/billing");
  if (loading) return <Loading />;
  return (
    <div className="space-y-4" data-testid="demo-billing">
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Invoices" value={data?.totals.count ?? 0} testid="bill-count" />
        <Stat label="Paid" value={money(data?.totals.paid)} testid="bill-paid" />
        <Stat label="Outstanding" value={money(data?.totals.outstanding)} tone="orange" testid="bill-outstanding" />
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <Table>
          <TableHeader><TableRow><TableHead>Job #</TableHead><TableHead>Client</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {(data?.invoices || []).map((inv, i) => (
              <TableRow key={i} data-testid={`invoice-row-${i}`}>
                <TableCell className="font-mono text-xs font-semibold">{inv.job_number}</TableCell>
                <TableCell className="text-slate-700">{inv.client}</TableCell>
                <TableCell className="text-right font-semibold">{money(inv.amount)}</TableCell>
                <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${inv.status === "paid" ? "bg-emerald-50 text-emerald-700" : inv.status === "invoiced" ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-600"}`}>{inv.status}</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* ---------------- Reporting ---------------- */
const BAR_BG = { sky: "bg-sky-500", indigo: "bg-indigo-500", violet: "bg-violet-500", emerald: "bg-emerald-500" };
function Bar({ label, value, max, tone = "sky" }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-28 shrink-0 text-xs text-slate-500">{label}</span>
      <div className="h-4 flex-1 overflow-hidden rounded bg-slate-100">
        <div className={`h-full ${BAR_BG[tone] || BAR_BG.sky}`} style={{ width: `${max ? (value / max) * 100 : 0}%` }} />
      </div>
      <span className="w-8 text-right text-xs font-semibold text-slate-700">{value}</span>
    </div>
  );
}
function ReportingView() {
  const { data, loading } = useDemo("/agency/demo/reporting");
  if (loading) return <Loading />;
  if (!data) return null;
  const maxStatus = Math.max(...Object.values(data.by_status), 1);
  const maxMonth = Math.max(...Object.values(data.by_month), 1);
  return (
    <div className="space-y-6" data-testid="demo-reporting">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Served Rate" value={`${data.served_rate_pct}%`} tone="emerald" testid="report-served-rate" />
        <Stat label="Avg Attempts" value={data.avg_attempts} testid="report-avg-attempts" />
        <Stat label="Completed" value={data.completed} testid="report-completed" />
        <Stat label="ARPS Revenue" value={money(data.arps_revenue)} testid="report-revenue" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Assignments by status</p>
          <div className="mt-4 space-y-2">{Object.keys(STATUS).filter((s) => data.by_status[s]).map((s) => <Bar key={s} label={STATUS[s].label} value={data.by_status[s]} max={maxStatus} />)}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Volume by month</p>
          <div className="mt-4 space-y-2">{Object.keys(data.by_month).map((m) => <Bar key={m} label={m} value={data.by_month[m]} max={maxMonth} tone="indigo" />)}</div>
          <p className="mt-6 text-xs font-bold uppercase tracking-widest text-slate-400">By service type</p>
          <div className="mt-3 space-y-2">{Object.keys(data.by_service_type).map((t) => <Bar key={t} label={t} value={data.by_service_type[t]} max={data.total_assignments} tone="violet" />)}</div>
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "clients", label: "Clients", icon: Users },
  { key: "assignments", label: "Assignments", icon: ClipboardList },
  { key: "providers", label: "Providers", icon: UserCog },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "billing", label: "Billing", icon: Receipt },
  { key: "reporting", label: "Reporting", icon: BarChart3 },
];

const TOUR_KEY = "arps_demo_tour_seen_v1";
const TOUR_STEPS = [
  { tab: null, selector: null, title: "Welcome to your ARPS evaluation", body: "This is a live, fully isolated demo agency already operating on ARPS — 100 assignments, 10 providers and complete billing. Take a 20-second tour of the essentials." },
  { tab: "dashboard", selector: '[data-testid="dashboard-pipeline"]', title: "Your assignment pipeline", body: "Every assignment flows through these stages — New, Assigned, Attempted, Served, Returns and Billing. The counts update live from your data." },
  { tab: "assignments", selector: '[data-testid^="assignment-row-"]', title: "Open any assignment", body: "Click a row to see the full service lifecycle end-to-end: Client → Assignment → Documents → Provider → Attempts → Evidence → Return → Billing → Compensation." },
  { tab: "reporting", selector: '[data-testid="demo-reporting"]', title: "Reporting & analytics", body: "Served rate, average attempts, monthly volume and revenue — all computed from your real assignment activity." },
];

function GuidedTour({ steps, onTab, onDone }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps[i];

  useEffect(() => {
    let cancelled = false;
    if (step.tab) onTab(step.tab);
    const measure = () => {
      if (cancelled) return;
      if (!step.selector) { setRect(null); return; }
      const el = document.querySelector(step.selector);
      if (!el) { setRect(null); return; }
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      setTimeout(() => { if (!cancelled) setRect(el.getBoundingClientRect()); }, 320);
    };
    const t = setTimeout(measure, step.tab ? 380 : 0);
    window.addEventListener("resize", measure);
    return () => { cancelled = true; clearTimeout(t); window.removeEventListener("resize", measure); };
  }, [i]); // eslint-disable-line

  const finish = () => { try { localStorage.setItem(TOUR_KEY, "1"); } catch {} onDone(); };
  const isLast = i === steps.length - 1;

  let cardStyle;
  if (rect) {
    const below = rect.bottom + 200 < window.innerHeight;
    cardStyle = below
      ? { top: rect.bottom + 14, left: Math.min(Math.max(rect.left, 16), window.innerWidth - 360) }
      : { top: Math.max(rect.top - 200, 16), left: Math.min(Math.max(rect.left, 16), window.innerWidth - 360) };
  } else {
    cardStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  }

  return (
    <div className="fixed inset-0 z-[100]" data-testid="demo-guided-tour">
      {/* dim + spotlight */}
      {rect ? (
        <div className="pointer-events-none fixed rounded-xl ring-4 ring-sky-400 transition-all duration-300"
          style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12, boxShadow: "0 0 0 9999px rgba(15,23,42,0.55)" }} />
      ) : (
        <div className="pointer-events-none fixed inset-0" style={{ background: "rgba(15,23,42,0.55)" }} />
      )}
      {/* tooltip card */}
      <div className="fixed w-[340px] max-w-[92vw] rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl" style={cardStyle}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 text-sky-600"><Sparkles className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-widest">Guided Tour · {i + 1}/{steps.length}</span></div>
          <button data-testid="tour-skip" onClick={finish} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"><X className="h-4 w-4" /></button>
        </div>
        <h3 className="mt-2 font-display text-lg font-bold text-slate-900">{step.title}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{step.body}</p>
        <div className="mt-4 flex items-center justify-between">
          <button data-testid="tour-skip-link" onClick={finish} className="text-xs font-medium text-slate-400 hover:text-slate-600">Skip tour</button>
          <div className="flex items-center gap-2">
            {i > 0 && <Button data-testid="tour-back" variant="outline" size="sm" onClick={() => setI((n) => n - 1)}><ArrowLeft className="mr-1 h-3.5 w-3.5" />Back</Button>}
            {isLast
              ? <Button data-testid="tour-finish" size="sm" onClick={finish} className="bg-sky-600 text-white hover:bg-sky-700">Explore ARPS</Button>
              : <Button data-testid="tour-next" size="sm" onClick={() => setI((n) => n + 1)} className="bg-sky-600 text-white hover:bg-sky-700">Next<ArrowRight className="ml-1 h-3.5 w-3.5" /></Button>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AgencyDemoPortal() {
  const [tab, setTab] = useState("dashboard");
  const [openId, setOpenId] = useState(null);
  const [tourOn, setTourOn] = useState(() => { try { return !localStorage.getItem(TOUR_KEY); } catch { return false; } });
  return (
    <div data-testid="agency-demo-portal">
      <div className="mb-3 flex justify-end">
        <Button data-testid="start-tour-btn" variant="outline" size="sm" onClick={() => { setTab("dashboard"); setTourOn(true); }}>
          <Sparkles className="mr-1.5 h-4 w-4 text-sky-500" />Take a tour
        </Button>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-slate-100 p-1">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`} className="gap-1.5 data-[state=active]:bg-white">
              <t.icon className="h-4 w-4" />{t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="dashboard" className="mt-6"><DashboardView /></TabsContent>
        <TabsContent value="clients" className="mt-6"><ClientsView /></TabsContent>
        <TabsContent value="assignments" className="mt-6"><AssignmentsView onOpen={setOpenId} /></TabsContent>
        <TabsContent value="providers" className="mt-6"><ProvidersView /></TabsContent>
        <TabsContent value="documents" className="mt-6"><DocumentsView /></TabsContent>
        <TabsContent value="billing" className="mt-6"><BillingView /></TabsContent>
        <TabsContent value="reporting" className="mt-6"><ReportingView /></TabsContent>
      </Tabs>
      {openId && <AssignmentDetail id={openId} onClose={() => setOpenId(null)} />}
      {tourOn && <GuidedTour steps={TOUR_STEPS} onTab={setTab} onDone={() => setTourOn(false)} />}
    </div>
  );
}
